import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { MfaMonitorService } from '../modules/auth/services/mfa-monitor.service';
import { PortalToastrService } from './portal-toastr.service';

/** Matches API HighRiskOperatorGuard::HIGH_RISK_PLAN_ACTIONS. */
export const MS365_HIGH_RISK_PLAN_ACTIONS = [
  'ResetPassword',
  'IssueTemporaryAccessPass',
  'RevokeSignInSessions',
  'SetFidoPolicy',
  'SetTemporaryAccessPassPolicy',
  'UpsertConditionalAccess',
  'EnableDkimSigning',
  'DeleteDomain',
  'ForceDeleteDomain',
] as const;

@Injectable({ providedIn: 'root' })
export class HighRiskStepUpService {
  private static readonly MS365_HIGH_RISK = new Set<string>(MS365_HIGH_RISK_PLAN_ACTIONS);

  /** Prevent overlapping MFA prompts from interceptor + callers. */
  private stepUpInFlight: Observable<boolean> | null = null;

  constructor(
    private mfaMonitor: MfaMonitorService,
    private translate: TranslateService,
    private toastr: PortalToastrService
  ) {}

  isMs365HighRiskActionType(type: string | null | undefined): boolean {
    return HighRiskStepUpService.MS365_HIGH_RISK.has(String(type || '').trim());
  }

  /** @deprecated Use isMs365HighRiskActionType */
  isHighRiskActionType(type: string | null | undefined): boolean {
    return this.isMs365HighRiskActionType(type);
  }

  planNeedsStepUp(plan: Record<string, unknown> | null | undefined): boolean {
    if (!plan) {
      return true;
    }
    const risk = String(plan['risk'] ?? '').trim();
    if (risk && risk.toLowerCase() === 'high') {
      return true;
    }
    const actions = plan['actions'];
    if (Array.isArray(actions)) {
      for (const row of actions) {
        const type =
          row && typeof row === 'object'
            ? String((row as Record<string, unknown>)['type'] ?? '')
            : '';
        if (this.isMs365HighRiskActionType(type)) {
          return true;
        }
      }
    }
    return !risk;
  }

  errorCode(err: unknown): string {
    const body = (err as { error?: Record<string, unknown> })?.error;
    if (!body || typeof body !== 'object') {
      return '';
    }
    const top = String(body['code'] || '').trim();
    if (top) {
      return top;
    }
    const nested = body['data'];
    if (nested && typeof nested === 'object') {
      return String((nested as Record<string, unknown>)['code'] || '').trim();
    }
    return '';
  }

  isMfaStepUpError(err: unknown): boolean {
    const code = this.errorCode(err);
    if (code === 'high_risk_mfa_required') {
      return true;
    }
    return /re-verify MFA|no recent verification|must be at most \d+ seconds old/i.test(
      this.errorMessage(err)
    );
  }

  isIpDeniedError(err: unknown): boolean {
    const code = this.errorCode(err);
    if (code === 'high_risk_ip_denied') {
      return true;
    }
    return /ADMIN_ALLOWED_IPS|admin_ip_list/i.test(this.errorMessage(err));
  }

  isEnrollmentError(err: unknown): boolean {
    const code = this.errorCode(err);
    if (code === 'high_risk_mfa_enrollment_required') {
      return true;
    }
    return /MFA or passkey enrollment is required/i.test(this.errorMessage(err));
  }

  errorMessage(err: unknown): string {
    const body = (err as { error?: Record<string, unknown>; message?: string })?.error;
    if (body && typeof body === 'object') {
      const top = body['error'] || body['message'];
      if (typeof top === 'string' && top.trim()) {
        return this.sanitizeUpstreamMessage(top.trim());
      }
      const nested = body['data'];
      if (nested && typeof nested === 'object') {
        const n = (nested as Record<string, unknown>)['error'] || (nested as Record<string, unknown>)['message'];
        if (typeof n === 'string' && n.trim()) {
          return this.sanitizeUpstreamMessage(n.trim());
        }
      }
    }
    return this.sanitizeUpstreamMessage(String((err as { message?: string })?.message || ''));
  }

  /**
   * Collapse Invoke-MgGraphRequest HTTP dumps into a short Graph code/message.
   */
  sanitizeUpstreamMessage(raw: string): string {
    const text = String(raw || '').trim();
    if (!text) {
      return '';
    }

    const errorIdx = text.indexOf('{"error"');
    const brace = errorIdx >= 0 ? errorIdx : text.indexOf('{ "error"');
    if (brace >= 0) {
      try {
        const json = JSON.parse(text.slice(brace)) as {
          error?: { code?: string; message?: string };
        };
        const code = String(json?.error?.code || '').trim();
        const detail = String(json?.error?.message || '').trim();
        if (code && detail) {
          return this.withPermissionHint(`${code}: ${detail}`, text);
        }
        if (detail) {
          return this.withPermissionHint(detail, text);
        }
        if (code) {
          return this.withPermissionHint(code, text);
        }
      } catch {
        // fall through
      }
    }

    if (/^(GET|POST|PATCH|PUT|DELETE)\s+https:\/\/graph\.microsoft\.com/im.test(text)) {
      const request = text.match(/^(GET|POST|PATCH|PUT|DELETE)\s+https:\/\/graph\.microsoft\.com[^\r\n]+/im)?.[0];
      const status = text.match(/^HTTP\/\d(?:\.\d)?\s+\d{3}[^\r\n]*/im)?.[0];
      const compact = [request, status].filter(Boolean).join(' → ');
      return this.withPermissionHint(compact || text.slice(0, 180), text);
    }

    if (text.length > 280) {
      return text.slice(0, 277) + '…';
    }
    return text;
  }

  private withPermissionHint(summary: string, raw: string): string {
    const denied = /Forbidden|accessDenied|Authorization_RequestDenied|Access denied|Insufficient privileges|Request Authorization failed|403/i.test(
      `${summary}\n${raw}`
    );
    if (/UserCredentialPolicy|does not allow creating or updating this authentication method|AuthMethodType\s*\[TemporaryAccessPass\]/i.test(
      `${summary}\n${raw}`
    )) {
      return `${summary} — Enable Temporary Access Pass in Entra ID → Authentication methods (must not be disabled), then retry.`;
    }
    if (!denied) {
      return summary;
    }
    if (/temporaryAccessPassMethods/i.test(raw)) {
      return `${summary} — Grant UserAuthenticationMethod.ReadWrite.All on the management app, admin-consent in the customer tenant, ensure TAP is enabled in Entra, then retry.`;
    }
    if (/revokeSignInSessions/i.test(raw)) {
      return `${summary} — Grant User.RevokeSessions.All on the management app, admin-consent in the customer tenant, then retry.`;
    }
    if (/passwordProfile|\/users\/[^/]+$/i.test(raw) && /PATCH/i.test(raw)) {
      return `${summary} — Grant User-PasswordProfile.ReadWrite.All on the management app, admin-consent in the customer tenant, then retry.`;
    }
    return summary;
  }

  userFacingError(err: unknown): string {
    if (this.isIpDeniedError(err)) {
      return this.translate.instant('COMMON.HIGH_RISK_IP_DENIED');
    }
    if (this.isEnrollmentError(err)) {
      return this.translate.instant('COMMON.HIGH_RISK_MFA_ENROLLMENT');
    }
    if (this.isMfaStepUpError(err)) {
      return this.translate.instant('COMMON.HIGH_RISK_MFA_REQUIRED');
    }
    return this.errorMessage(err) || this.translate.instant('COMMON.ERROR');
  }

  isCancelledError(err: unknown): boolean {
    const msg = String((err as { message?: string })?.message || '');
    return msg.includes(this.translate.instant('COMMON.HIGH_RISK_MFA_CANCELLED'));
  }

  /** Shared MFA prompt (deduped). */
  promptHighRiskMfa(force = false): Observable<boolean> {
    if (this.stepUpInFlight) {
      return this.stepUpInFlight;
    }
    const prompt$ = this.mfaMonitor.ensureHighRiskMfa(force);
    this.stepUpInFlight = prompt$;
    return new Observable<boolean>((subscriber) => {
      const sub = prompt$.subscribe({
        next: (ok) => {
          this.stepUpInFlight = null;
          subscriber.next(ok);
          subscriber.complete();
        },
        error: (err) => {
          this.stepUpInFlight = null;
          subscriber.error(err);
        },
      });
      return () => sub.unsubscribe();
    });
  }

  /**
   * Ensure MFA ≤5min (when needed), run the op, and on MFA 403 force re-verify once then retry.
   */
  run<T>(op: () => Observable<T>, opts?: { requireStepUp?: boolean }): Observable<T> {
    const requireStepUp = opts?.requireStepUp !== false;
    const cancelled = () =>
      throwError(() => new Error(this.translate.instant('COMMON.HIGH_RISK_MFA_CANCELLED')));

    const start$ = requireStepUp
      ? this.promptHighRiskMfa().pipe(
          switchMap((ok) => {
            if (!ok) {
              this.toastr.warning(this.translate.instant('COMMON.HIGH_RISK_MFA_CANCELLED'));
              return cancelled();
            }
            return op();
          })
        )
      : op();

    return start$.pipe(
      catchError((err) => {
        if (this.isCancelledError(err)) {
          return throwError(() => err);
        }
        if (this.isIpDeniedError(err) || this.isEnrollmentError(err)) {
          this.toastr.error(this.userFacingError(err));
          return throwError(() => err);
        }
        // MFA 403 is handled by HighRiskStepUpInterceptor (prompt + single retry).
        return throwError(() => err);
      })
    );
  }
}
